<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-soa-main">
    <div class="soa-options">
      <div>
        <RadioButtonSet
          ref="selectby"
          title="Select By"
          :buttons="selectByOptions"
          labelKey="label"
          :selected.sync="selectedSelectBy"
          :errorMessage.sync="selectByErr"
          :isHorizontal="true"
          :isRequired="true"
          style="margin-right: 5px"
          @radioChanged="selectByRadioChanged"
        />
      </div>
      <div class="soa-tablebox-container">
        <TableBox
          v-if="showSelectionTable"
          :title="tableBoxTitle"
          :tableData.sync="selectionTableData"
          :selectedRow.sync="selectedOption"
          :maxSelection="-1"
          :loading="tableLoading"
          :showSelectAll="true"
          :columnWidth="[50, 150, 300, 100, 130, 130, 150, 150, 150]"
        />
      </div>
      <div style="margin-top: 5px">
        <div>
          <BoxContainer class="date-options-container" style="width: auto; margin-left: 0px; margin-bottom: 5px">
            <div style="display: flex">
              <DateTimePicker
                ref="asatdate"
                placeholder="As At"
                :value.sync="asAtDate"
                :errorMessage.sync="asAtDateErr"
                :showTime="false"
                :isRequired="true"
                :minDate="new Date('1900-01-01')"
                :maxDate="new Date('2079-06-06')"
              />
              <div style="display: flex; margin-top: 5px; margin-left: 30px">
                <CheckBoxV2
                  title="Include Current Month"
                  :isChecked.sync="includeCurrMthChecked"
                  :isDisabled="offsetFormatChecked"
                />
                <!-- records till month end: as at data will change to last date of month when call api to get details -->
                <CheckBoxV2 title="Records till Month-End" :isChecked.sync="rcdsTillCurrMthChecked" />
              </div>
            </div>
          </BoxContainer>
        </div>
        <div style="display: flex; height: 100px">
          <Filters
            title="Doc Config"
            :tableData="docConfigList"
            itemKey="Job Type"
            :selectedObject.sync="selectedJobType"
            @confirmClicked="docConfigConfirm"
            :tableBoxHideColumns="['JobTypeID']"
            style="height: 100%; width: 50%"
          />
          <Filters
            title="Customer Status"
            :tableData="statusList"
            itemKey="Description"
            :selectedObject.sync="selectedStatus"
            @confirmClicked="statusConfirm"
            :isDisabled="statusFilterDisabled"
            style="height: 100%; width: 50%"
          />
        </div>
        <div style="display: flex; margin-top: 5px">
          <Dropdown
            ref="reportformat"
            placeholder="Report Format"
            :items="reportFormatItems"
            itemKey="option"
            :selectedObject.sync="selectedReportFormat"
            :isRequired="true"
            :errorMessage.sync="reportFormatErr"
            style="width: 50%"
          />
          <CheckBoxV2 title="Offset Format" :isChecked.sync="offsetFormatChecked" style="margin-left: 10px" />
        </div>
        <div style="display: flex; margin-top: 5px">
          <div>
            <CheckBoxV2 title="By Currency" :isChecked.sync="byCurrencyChecked" />
            <CheckBoxV2 title="Based on Due Date" :isChecked.sync="basedOnDueDateChecked" />
            <CheckBoxV2
              title="Order by Reference No"
              :isChecked.sync="orderByInvNoChecked"
              :isDisabled="closeAccountChecked || offsetFormatChecked"
            />
          </div>
          <div>
            <CheckBoxV2
              title="Show Future Payment"
              :isChecked.sync="showFuturePaymentChecked"
              :isDisabled="disableShowFuturePay"
            />
            <CheckBoxV2 title="Show Cash Term Only" :isChecked.sync="showCashTermOnlyChecked" />
            <CheckBoxV2 title="Show Advance Only" :isChecked.sync="showAdvOnlyChecked" />
          </div>
          <div>
            <CheckBoxV2
              title="Close Account"
              :isChecked.sync="closeAccountChecked"
              :isDisabled="ageingFormatSelected || byCurrencyChecked || offsetFormatChecked"
            />
            <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showCoLetterHeadChecked" />
          </div>
        </div>
        <div style="display: flex">
          <BoxContainer
            title="Hide Debtors"
            :isCheckBox="true"
            :isChecked.sync="hideDebtorsChecked"
            style="margin-top: 10px; width: 50%"
          >
            <Dropdown
              ref="balance"
              placeholder="with balance"
              :items="balanceItems"
              itemKey="option"
              :selectedObject.sync="selectedBalance"
              :isDisabled="!hideDebtorsChecked"
              :isRequired="hideDebtorsChecked"
              :errorMessage.sync="balanceErr"
              validator="text,5"
            />
          </BoxContainer>

          <RadioButtonSet
            v-if="ageingFormatSelected"
            ref="totalaging"
            title="Total Aging"
            :buttons="totalAgingItems"
            labelKey="aging"
            :selected.sync="selectedTotalAging"
            :errorMessage.sync="totalAgingErr"
            :isHorizontal="true"
            style="margin-left: 10px; width: 50%; align-self: center"
          />
        </div>
        <div style="display: flex; flex-direction: row; justify-content: flex-end; margin-top: 5px">
          <button @click="previewSOAClicked">Preview Statement of Account</button>
        </div>
      </div>
    </div>
    <div class="soa-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canToggleBackground="false"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        @shareClicked="designerV3ShareClicked"
        downloadName="StatementOfAccounts"
        ref="designerV3"
      />
      <div v-else class="user-instructions">
        <span>Click on <strong>Preview Statement Of Account</strong> to get started</span>
      </div>
    </div>
    <PopupV2
      class="email-soa-popup"
      :show="showEmailSOAPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailSOAPopupButtons"
      title="Email Statement Of Accounts"
      width="60%"
      @buttonClicked="emailSOAPopupButtonClicked"
    >
      <div class="email-soa-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailSOAPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailSOAPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailSOAPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailSOAPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailSOAPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    tableBoxTitle: function () {
      let title = '';
      if (this.selectedSelectBy) {
        switch (this.selectedSelectBy.key) {
          case 'customer':
            title = 'Customer';
            break;

          case 'glacc':
            title = 'GL Account Code';
            break;

          case 'status':
            title = 'Customer Status';
            break;

          case 'salesman':
            title = 'Sales Person';
            break;
        }
      }
      return title;
    },
    ageingFormatSelected: function () {
      return this.selectedReportFormat && ['1', '2'].includes(this.selectedReportFormat.apiKey);
    },
    statusFilterDisabled: function () {
      return this.selectedSelectBy && this.selectedSelectBy.key == 'salesman';
    },
    disableShowFuturePay: function () {
      return this.selectedReportFormat && !(['3', '4'].includes(this.selectedReportFormat['apiKey']));
    },
  },
  data: function () {
    return {
      userDetails: null,
      selectByOptions: [
        { label: 'Customer', key: 'customer' },
        { label: 'GL Account Code', key: 'glacc' },
        // { label: 'Status', key: 'status' },
        { label: 'Sales Person', key: 'salesman' },
      ],
      selectedSelectBy: null,
      selectByErr: '',

      tableLoading: false,
      showSelectionTable: true,
      selectionTableData: [],
      customerList: [],
      glAccList: [],
      statusList: [],
      salesPersonList: [],
      docConfigList: [],

      selectedOption: [],

      //#region Filters
      asAtDate: null,
      asAtDateErr: '',
      includeCurrMthChecked: false,
      rcdsTillCurrMthChecked: false,
      selectedJobType: [],
      selectedStatus: [],
      reportFormatItems: [
        { option: "Customer's Copy", apiKey: '0' },
        { option: 'Ageing Summary', apiKey: '1' },
        { option: 'Ageing Detail', apiKey: '2' },
        // { option: 'Collection Summary', apiKey: '3' },
        // { option: 'Collection Details', apiKey: '4' },
        // { option: 'Period Statistic', apiKey: '5' },
      ],
      selectedReportFormat: null,
      reportFormatErr: '',
      offsetFormatChecked: false, // If checked, filters disabled: Include Current Month, Order by Reference No, Close Account,
      byCurrencyChecked: false, // If checked, filters disabled: Clsoe Account
      basedOnDueDateChecked: false,
      orderByInvNoChecked: false,
      showFuturePaymentChecked: false,
      showCashTermOnlyChecked: false,
      showAdvOnlyChecked: false,
      closeAccountChecked: false,
      showCoLetterHeadChecked: true,
      totalAgingItems: [{ aging: '6' }, { aging: '12' }],
      selectedTotalAging: null,
      totalAgingErr: '',
      hideDebtorsChecked: false,
      balanceItems: [
        { option: '= 0', apiKey: 'equals' },
        { option: '<= 0', apiKey: 'lessequal' },
      ],
      selectedBalance: null,
      balanceErr: '',
      //#endregion

      //#region DesignerV3
      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },
      //#endregion

      //#region email
      showEmailSOAPopup: false,
      emailSOAPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailSOAPopupToMails: [],
      emailSOAPopupCCMails: [],
      emailSOAPopupBCCMails: [],
      emailSOAPopupSubjectInputValue: '',
      emailSOAPopupMessageText: '',
      //#endregion

      //#region settingssystem
      custCopyAgeingSummaryFormat: '0',
      custCopyShowInterestStmt: false,
      showTermColumn: false,
      showContactRow: true,
      ageing6HeaderFmt: '0',
      jobnoColSetting: '1', // 0: hide, 1: job no, 2: cust ref
      ageing12HideAdv: false,
      ageing12ShowCrLimit: false,
      ageing6Show5Buckets: false,
      //#endregion
    };
  },
  props: {},
  methods: {
    selectByRadioChanged: function (val) {
      this.selectedOption = [];
      this.showSelectionTable = false;
      switch (val['key']) {
        case 'customer':
          this.selectionTableData = [...this.customerList];
          break;

        case 'glacc':
          this.selectionTableData = [...this.glAccList];
          break;

        case 'status':
          this.selectionTableData = [...this.statusList];
          break;

        case 'salesman':
          this.selectionTableData = [...this.salesPersonList];
          break;
      }

      this.$nextTick(() => {
        this.showSelectionTable = true;
      });
    },

    docConfigConfirm: function (items) {
      this.selectedJobType = items;
    },

    statusConfirm: function (items) {
      this.selectedStatus = items;
    },

    getLastDateOfMonth: function (date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    },

    previewSOAClicked: async function () {
      // Validations
      if (this.selectedOption.length == 0) {
        await this.$showAlert(
          'error',
          `No ${this.tableBoxTitle} selected`,
          `Please select at least one ${this.tableBoxTitle} to proceed`,
        );
        return;
      }

      let error = false;
      if (!this.selectedReportFormat) {
        this.reportFormatErr = 'Report format is required.';
        this.requiredError.push({ key: 'reportformat', value: this.reportFormatErr });
        error = true;
      }

      if (!this.asAtDate) {
        this.asAtDateErr = 'As At date is required.';
        this.requiredError.push({ key: 'asatdate', value: this.asAtDateErr });
        error = true;
      } else if (this.asAtDate < new Date('1900-01-01')) {
        this.asAtDateErr = 'Invalid As At date.';
        this.requiredError.push({ key: 'asatdate', value: this.asAtDateErr });
        error = true;
      } else if (this.asAtDate > new Date('2079-06-06')) {
        this.asAtDateErr = 'Invalid As At date.';
        this.requiredError.push({ key: 'asatdate', value: this.asAtDateErr });
        error = true;
      }

      if (this.hideDebtorsChecked && !this.selectedBalance) {
        this.balanceErr = 'Balance is required.';
        this.requiredError.push({ key: 'balance', value: this.balanceErr });
        error = true;
      }

      if (error) return;

      // Set as at date to last day of the month if records till month end checked
      let asatDateToPost = new Date(this.asAtDate).formatString('yyyy-MM-dd');
      if (this.rcdsTillCurrMthChecked) {
        asatDateToPost = this.getLastDateOfMonth(this.asAtDate).formatString('yyyy-MM-dd');
      }

      const optionKey = this.getOptionKey();
      // const selectedAll = this.selectedOption.length == this.selectionTableData.length;
      const requestBody = {
        asat: asatDateToPost,
        tmptblname: `${this.userDetails.LoginUserId}_${new Date().formatString('yyyyMMdd')}`,
        rptformat: this.selectedReportFormat ? this.selectedReportFormat['apiKey'] : '',
        offsetformat: this.offsetFormatChecked ? '1' : '0',
        bycurrency: this.byCurrencyChecked ? '1' : '0',
        postpay: !this.disableShowFuturePay && this.showFuturePaymentChecked ? '1' : '0',
        duedate: this.basedOnDueDateChecked ? '1' : '0',
        curmonth: this.offsetFormatChecked || this.includeCurrMthChecked ? '1' : '0',
        showcashonly: this.showCashTermOnlyChecked ? '1' : '0',
        showadvonly: this.showAdvOnlyChecked ? '1' : '0',
        orderbyinvno: !this.offsetFormatChecked && this.orderByInvNoChecked ? '1' : '0',
        closeaccount: !this.offsetFormatChecked && this.closeAccountChecked ? '1' : '0',
        showall: 'N', // for period statistics format (N/A for now)
        jobconfigarray: this.selectedJobType.length > 0 ? this.selectedJobType.map((j) => j.JobTypeID).join(',') : '',
        jobconfigmain:
          this.selectedJobType.length > 0 ? this.docConfigList.find((d) => d['#'] != 0)?.JobTypeID.toString() : '',
        hidezerobaldebtors: this.hideDebtorsChecked ? '1' : '0',
        zerobalcondition: this.hideDebtorsChecked && this.selectedBalance ? this.selectedBalance['apiKey'] : '0',
        showcompanyletterhead: this.showCoLetterHeadChecked ? '1' : '0',
        totalaging:
          ['1', '2'].includes(this.selectedReportFormat.apiKey) ?
            this.selectedTotalAging ?
              this.selectedTotalAging['aging']
            : '6'
          : '',
        filtertype: this.selectedSelectBy ? this.selectedSelectBy['key'] : '',
        selectionarray: this.selectedOption.map((obj) => obj[optionKey]).join(','),
        selectall: this.selectedOption.length == this.selectionTableData.length ? '1' : '0',
        coshortname: this.userDetails.CoRefname,
        currency: '',
        statusarray:
          this.statusFilterDisabled ? ''
          : this.selectedStatus.length > 0 ? this.selectedStatus.map((s) => s['Status Code']).join(',')
          : '',
      };

      this.$fullscreenLoading('Getting Statement of Accounts');
      let soaDetails = { General: {} };

      // by currency means different currency are in different reports, i.e. 1 customer can have multiple SOA if contains documents in different currency
      if (this.byCurrencyChecked) {
        let key = '';
        let currencyList = await this.$apiCallV2('/StatementOfAccounts/GetArAgingProc', requestBody);
        currencyList = currencyList.map((c) => c.currency);
        const allGroups = [];
        for (const [index, curr] of currencyList.entries()) {
          requestBody.currency = curr;
          const soa = await this.$apiCallV2('/StatementOfAccounts/GetStatementOfAccounts', requestBody);

          if (Array.isArray(soa) && soa[0].Results === '1') {
            this.$showAlert('error', 'Error Printing Statement of Accounts', soa[0].ErrMsg, [{ text: 'OK' }]);
            this.$fullscreenLoading();
            return;
          }

          key = Object.keys(soa['Statement of Accounts'])?.[1];
          if (index == 0) {
            soaDetails.General = soa['Statement of Accounts']['General'];
          }

          soa['Statement of Accounts'][key].forEach((s) => allGroups.push(s));
        }
        soaDetails[key] = allGroups;
      } else {
        soaDetails = await this.$apiCallV2('/StatementOfAccounts/GetStatementOfAccounts', requestBody);
        this.$fullscreenLoading();
        if (Array.isArray(soaDetails) && soaDetails[0].Results === '1') {
          this.$showAlert('error', 'Error Printing Statement of Accounts', soaDetails[0].ErrMsg, [{ text: 'OK' }]);
          this.$fullscreenLoading();
          return;
        }
        soaDetails = soaDetails['Statement of Accounts'];

        switch (this.selectedReportFormat.apiKey) {
          case '0':
            if (!soaDetails.Customer || soaDetails.Customer.length == 0) {
              this.$showAlert('warning', 'No Data', 'No data found for the selected criteria');
              this.$fullscreenLoading();
              return;
            }
            break;

          case '1':
          case '2':
            if (!soaDetails.StatementOfAccount[0].Group || soaDetails.StatementOfAccount[0].Group.length == 0) {
              this.$showAlert('warning', 'No Data', 'No data found for the selected criteria');
              this.$fullscreenLoading();
              return;
            }
            break;
        }
      }

      if (this.byCurrencyChecked || this.showAdvOnlyChecked) {
        switch (this.selectedReportFormat.apiKey) {
          case '0':
            soaDetails.Customer = soaDetails.Customer.filter((s) => s['Statements'] && s['Statements'].length > 0);
            break;

          case '1':
          case '2':
            if (this.selectedSelectBy['key'] == 'salesman') {
              soaDetails['StatementOfAccount'].forEach((soa) => {
                soa['Group'] = soa['Group'].filter((g) => g['Customer'] && g['Customer'].length > 0);
              });
              soaDetails['StatementOfAccount'] = soaDetails.StatementOfAccount.filter(
                (g) => g['Group'] && g['Group'].length > 0,
              );
            } else {
              soaDetails['StatementOfAccount'] = soaDetails.StatementOfAccount.filter(
                (g) => g['Group'] && g['Group'].length > 0,
              );
            }
            break;
        }
      }

      switch (this.selectedReportFormat.apiKey) {
        case '0': {
          const formattedDetails = this.getCustomerCopyFormat(soaDetails);
          this.designerV3ReportFiles = Array.from(
            { length: formattedDetails.Customer.length },
            () => 'SOA_CustomerCopy.json',
          );
          this.designerV3ReportData = formattedDetails.Customer;
          this.designerV3SharedData = formattedDetails.General;
          break;
        }

        case '1':
        case '2': {
          const formattedDetails =
            this.selectedTotalAging['aging'] == '6' ?
              this.getAgeing6Format(soaDetails)
            : this.getAgeing12Format(soaDetails);
          this.designerV3ReportFiles = Array.from({ length: formattedDetails.StatementOfAccount.length }, () =>
            this.selectedTotalAging['aging'] == '6' ? 'SOA_Ageing6.json' : 'SOA_Ageing12.json',
          );
          this.designerV3ReportData = formattedDetails.StatementOfAccount;
          this.designerV3SharedData = formattedDetails.General;
          break;
        }
      }

      if (this.designerV3ReportData.length == 0) {
        this.$showAlert('warning', 'No Data', 'No data found for the selected criteria');
      } else {
        this.designerV3SharedData['companyheader'] = this.userDetails['CoHeaderFin'] ? this.userDetails['CoHeaderFin'] : this.designerV3SharedData['companyheader'];
        this.showDesignerV3 = true;
      }
      this.$fullscreenLoading();
    },

    getCustomerCopyFormat: function (statements) {
      const soaDetails = statements;
      soaDetails.General['Show Company Letter Head'] = soaDetails.General['Show Company Letter Head'] !== '0';

      this.$fullscreenLoading('Preparing to Print');
      for (const soa of soaDetails.Customer) {
        const postcodeCity = [soa['postcode'], soa['city']].filter((s) => s && s.trim() != '').join(' ');
        const stateCountry = [soa['state'], soa['country']].filter((s) => s && s.trim() != '').join(', ');
        let address = [
          soa['addr1'] || '',
          soa['addr2'] || '',
          soa['addr3'] || '',
          soa['addr4'] || '',
          `${postcodeCity}`,
          `${stateCountry}`,
        ];

        if (soa['phone'] || soa['fax']) {
          address.push(`Tel: ${soa['phone']}     Fax: ${soa['fax']}`);
        }
        if (soa['contact']) {
          address.push(`Attn: ${soa['contact']}`);
        }

        address = address.filter((a) => a !== '').join('\n');
        soa['address'] = address;
        soa['asat'] = this.asAtDate.formatString(this.$store.state['preferredDateFormat']);
        soa['printdate'] = new Date().formatString(this.$store.state['preferredDateFormat']);
        soa['page'] = '{{jsreportPageNum}} of {{jsreportTotalPage}}';
        soa['currency'] = soa['currency'] || 'MYR';
        soa['showCustName'] = soaDetails.General['Co Short Name'] == 'FMH';
        soa['jobFilter'] = this.selectedJobType.length > 0;
        soa['cashOnly'] = this.showCashTermOnlyChecked ? '(CASH ONLY)' : '';
        if (this.selectedJobType.length > 0) {
          soa['docConfigFilter'] = this.selectedJobType.map((j) => j['Job Type']).join(', ');
        }

        if (!this.byCurrencyChecked) {
          soa['fAmtCaption'] = 'F.Amt.';
          soa['fCurrCaption'] = 'F.Curr.';
        }

        soa['showFCurrency'] =
          soa['Statements'] &&
          soa['Statements'].some((s) => s.fCurrency && s.fCurrency.trim() != '') &&
          !this.byCurrencyChecked;

        if (!soa['Statements']) {
          soa['Statements'] = [];
        }

        if (soa['Statements'].length > 0) {
          soa['Statements'] = soa['Statements'].map((txn) => {
            return {
              ...txn,
              refno: txn['refno'] === 'Bal b/f' ? `<b>${txn['refno']}</b>` : txn['refno'],
              Balance: txn['Balance'] ? this.formatAmount(txn['Balance']) : '',
              NetOpen: txn['NetOpen'] ? this.formatAmount(txn['NetOpen']) : '',
              creditAging: txn['creditAging'] ? this.formatAmount(txn['creditAging']) : '',
              fCredit: txn['fCredit'] && Number(txn['fCredit']) != 0 ? this.formatAmount(txn['fCredit']) : '',
              fDebit: txn['fDebit'] && Number(txn['fDebit']) != 0 ? this.formatAmount(txn['fDebit']) : '',
              fCurrency: txn['fCurrency'] && Number(txn['fCurrency']) != 0 ? this.formatAmount(txn['fCurrency']) : '',
            };
          });
        }

        soa['sumNetDr'] = this.formatAmount(soa['sumNetDr']);
        soa['sumNetCr'] = this.formatAmount(soa['sumNetCr']);
        soa['sumOut'] = this.formatAmount(soa['sumOut']);
        soa['sumAging45'] = this.formatAmount(soa['sumAging45']);
        soa['sumCrAging'] = this.formatAmount(soa['sumCrAging']);
        soa['sumAging0'] = this.formatAmount(soa['sumAging0']);
        soa['sumAging1'] = this.formatAmount(soa['sumAging1']);
        soa['sumAging2'] = this.formatAmount(soa['sumAging2']);
        soa['sumAging3'] = this.formatAmount(soa['sumAging3']);
        soa['sumAging4'] = this.formatAmount(soa['sumAging4']);
        soa['sumAging5'] = this.formatAmount(soa['sumAging5']);
        soa['sumAging6'] = this.formatAmount(soa['sumAging6']);
        soa['sumAging7'] = this.formatAmount(soa['sumAging7']);
        soa['sumAging8'] = this.formatAmount(soa['sumAging8']);
        soa['sumAging9'] = this.formatAmount(soa['sumAging9']);
        soa['sumAging10'] = this.formatAmount(soa['sumAging10']);
        soa['sumAging11'] = this.formatAmount(soa['sumAging11']);
        soa['lastColCap'] =
          (
            ['G-FORCE', 'GFLS', 'TNLS'].includes(soaDetails.General['Co Short Name']) ||
            this.custCopyAgeingSummaryFormat == '2'
          ) ?
            'CREDIT'
          : soa['mDuration6'];
        soa['lastColAmt'] =
          (
            ['G-FORCE', 'GFLS', 'TNLS'].includes(soaDetails.General['Co Short Name']) ||
            this.custCopyAgeingSummaryFormat == '2'
          ) ?
            soa['sumCrAging']
          : soa['sumAging5'];
        soa['showSummaryAmt'] = this.custCopyAgeingSummaryFormat == '1' || soaDetails.General['Co Short Name'] == 'TS';
        soa['show12Months'] = this.custCopyAgeingSummaryFormat == '1' || soaDetails.General['Co Short Name'] == 'TS';
        soa['interestStatement'] =
          this.custCopyShowInterestStmt || ['TS', 'TNLS', 'UFA', 'LOF'].includes(soaDetails.General['Co Short Name']);
      }
      return soaDetails;
    },

    getAgeing6Format: function (statements) {
      const optionKey = this.getOptionKey();
      const soaDetails = statements;
      const reportType = this.selectedReportFormat.apiKey == '1' ? 'Summary' : 'Detail';
      soaDetails.General['asAtDate'] = this.asAtDate.formatString(this.$store.state['preferredDateFormat']);
      soaDetails.General['reportType'] = reportType;
      soaDetails.General['filterType'] = this.selectedSelectBy['label'].toUpperCase();
      soaDetails.General['customerNo'] =
        this.selectedOption.length == this.selectionTableData.length ?
          'ALL'
        : this.selectedOption.map((o) => o[optionKey]).join(', ');
      soaDetails.General['custStatus'] = (this.selectedStatus.length == 0 ? this.statusList : this.selectedStatus)
        .map((s) => s['Description'])
        .filter((s) => s !== 'string')
        .join(', ');
      soaDetails.General['showTermColumn'] = this.showTermColumn;
      switch (this.jobnoColSetting) {
        case '0':
          soaDetails.General['jobnoTitle'] = '';
          break;

        case '2':
          soaDetails.General['jobnoTitle'] = 'Cust. Ref.';
          break;

        default:
          soaDetails.General['jobnoTitle'] = 'Job No';
          break;
      }
      soaDetails.General['mDuration6'] = this.ageing6Show5Buckets ? '' : soaDetails.General['mDuration6'];
      // Combine summary and detail format together in 1 template, so need a key to identify which table format to show
      soaDetails.General['summaryWithoutTerm'] = reportType == 'Summary' && !this.showTermColumn;
      soaDetails.General['summaryWithTerm'] = reportType == 'Summary' && this.showTermColumn;
      soaDetails.General['detailFormat'] = reportType == 'Detail';

      const soaGroups = soaDetails.StatementOfAccount;
      for (const soa of soaGroups) {
        soa['totalA0'] = this.formatAmount(soa['totalA0']);
        soa['totalA1'] = this.formatAmount(soa['totalA1']);
        soa['totalA2'] = this.formatAmount(soa['totalA2']);
        soa['totalA3'] = this.formatAmount(soa['totalA3']);
        soa['totalA4'] = this.formatAmount(soa['totalA4']);
        soa['totalAdv'] =
          this.ageing6Show5Buckets ? '' : (
            this.formatAmount(this.ageing6HeaderFmt === '1' ? soa['totalA5'] : soa['totalAdv'])
          );
        soa['totalAging'] = this.formatAmount(soa['totalAg']);

        const formattedGroups = [...soa.Group];
        soa._soaDetails = formattedGroups.map((grp) => {
          return {
            groupName: grp['fgroup'] ?? '',
            GAging0: this.formatAmount(grp['accTotalAging0'] || 0),
            GAging1: this.formatAmount(grp['accTotalAging1'] || 0),
            GAging2: this.formatAmount(grp['accTotalAging2'] || 0),
            GAging3: this.formatAmount(grp['accTotalAging3'] || 0),
            GAging4: this.formatAmount(grp['accTotalAging4'] || 0),
            GAgingAdv:
              this.ageing6Show5Buckets ? '' : (
                this.formatAmount((this.ageing6HeaderFmt === '1' ? grp['accTotalAging5'] : grp['accTotalAdv']) || 0)
              ),
            GAgingTot: this.formatAmount(grp['accTotal'] || 0),
            _customer: grp['Customer'].map((c) => {
              return {
                ...c,
                contactinfo:
                  this.showContactRow ? `Tel No: ${c['custphone'] || ''}  Contact: ${c['custcontact'] || ''}` : '',
                term: this.showTermColumn ? c['term'] || '' : '',
                Aging0: this.formatAmount(c['Aging0_NoAdv'] || '0'),
                Aging1: this.formatAmount(c['Aging1_NoAdv'] || '0'),
                Aging2: this.formatAmount(c['Aging2_NoAdv'] || '0'),
                Aging3: this.formatAmount(c['Aging3_NoAdv'] || '0'),
                Aging4: this.formatAmount(c['Aging4_NoAdv'] || '0'),
                AgingAdv:
                  this.ageing6Show5Buckets ? '' : (
                    this.formatAmount((this.ageing6HeaderFmt == '1' ? c['Aging5_NoAdv'] : c['Advance']) || '0')
                  ),
                AgingTot: this.formatAmount(c['amtTotalAgeing'] || '0'),
                _docStatements:
                  reportType == 'Detail' ?
                    c['Statements'].map((s) => {
                      return {
                        txndate:
                          s['txnDate'] ?
                            new Date(s['txnDate']).formatString(this.$store.state['preferredDateFormat'])
                          : '',
                        refno: s['refno'] || '',
                        adv: s['sadv'] || '',
                        jobno:
                          this.jobnoColSetting == '2' ? s['custref']
                          : this.jobnoColSetting == '0' ? ''
                          : s['JobNo'],
                        docterm: s['cTerm'] || '',
                        refAging0: this.formatAmount(s['Aging0_NoAdv'] || '0'),
                        refAging1: this.formatAmount(s['Aging1_NoAdv'] || '0'),
                        refAging2: this.formatAmount(s['Aging2_NoAdv'] || '0'),
                        refAging3: this.formatAmount(s['Aging3_NoAdv'] || '0'),
                        refAging4: this.formatAmount(s['Aging4_NoAdv'] || '0'),
                        refAgingAdv:
                          this.ageing6Show5Buckets ? '' : (
                            this.formatAmount((this.ageing6HeaderFmt === '1' ? s['Aging5_NoAdv'] : s['Advance']) || '0')
                          ),
                        balance: this.formatAmount(s['Balance'] || '0'),
                      };
                    })
                  : [],
              };
            }),
          };
        });
      }
      const formattedSOADetails = {
        General: soaDetails.General,
        StatementOfAccount: soaGroups,
      };
      return formattedSOADetails;
    },

    getAgeing12Format: function (statements) {
      const optionKey = this.getOptionKey();
      const soaDetails = statements;
      soaDetails.General['asAtDate'] = this.asAtDate.formatString(this.$store.state['preferredDateFormat']);
      soaDetails.General['reportType'] = this.selectedReportFormat.apiKey == '1' ? 'Summary' : 'Detail';
      soaDetails.General['filterType'] = this.selectedSelectBy['label'].toUpperCase();
      soaDetails.General['customerNo'] =
        this.selectedOption.length == this.selectionTableData.length - 1 ?
          'ALL'
        : this.selectedOption.map((o) => o[optionKey]).join(', ');
      soaDetails.General['custStatus'] = (this.selectedStatus.length == 0 ? this.statusList : this.selectedStatus)
        .map((s) => s['Description'])
        .filter((s) => s !== 'string')
        .join(', ');
      soaDetails.General['showTermColumn'] = this.showTermColumn;
      soaDetails.General['advTableHeader'] = this.ageing12HideAdv ? '' : 'ADVANCE';
      const soaGroups = soaDetails.StatementOfAccount;

      // Combine summary and detail format together in 1 template, so need a key to identify which table format to show
      const reportType = this.selectedReportFormat.apiKey == '1' ? 'Summary' : 'Detail';
      soaDetails.General['summaryWithoutCrLimit'] = reportType == 'Summary' && !this.ageing12ShowCrLimit;
      soaDetails.General['summaryWithCrLimit'] = reportType == 'Summary' && this.ageing12ShowCrLimit;
      soaDetails.General['detailWithoutCrLimit'] = reportType == 'Detail' && !this.ageing12ShowCrLimit;
      soaDetails.General['detailWithCrLimit'] = reportType == 'Detail' && this.ageing12ShowCrLimit;

      for (const soa of soaGroups) {
        for (let i = 0; i < 13; i++) {
          soa[`totalA${i}`] = this.formatAmount(soa[`totalA${i}`] || 0);
          soa[`perAg${i}`] = this.formatAmount(soa[`per${i}`] || 0);
        }

        soa['totalAdv'] = this.ageing12HideAdv ? '' : this.formatAmount(soa['totalAdv'] || 0);
        soa['totalAging'] = this.formatAmount(soa['totalAg'] || 0);
        soa['perAgAdv'] = this.ageing12HideAdv ? '' : this.formatAmount(soa['perAdv'] || 0);

        const formattedGroups = [...soa.Group];
        soa._soaDetails = formattedGroups.map((grp) => {
          return {
            groupName: grp['fgroup'] ?? '',
            GAging0: this.formatAmount(grp['accTotalAging0'] || 0),
            GAging1: this.formatAmount(grp['accTotalAging1'] || 0),
            GAging2: this.formatAmount(grp['accTotalAging2'] || 0),
            GAging3: this.formatAmount(grp['accTotalAging3'] || 0),
            GAging4: this.formatAmount(grp['accTotalAging4'] || 0),
            GAging5: this.formatAmount(grp['accTotalAging5'] || 0),
            GAging6: this.formatAmount(grp['accTotalAging6'] || 0),
            GAging7: this.formatAmount(grp['accTotalAging7'] || 0),
            GAging8: this.formatAmount(grp['accTotalAging8'] || 0),
            GAging9: this.formatAmount(grp['accTotalAging9'] || 0),
            GAging10: this.formatAmount(grp['accTotalAging10'] || 0),
            GAging11: this.formatAmount(grp['accTotalAging11'] || 0),
            GAging12: this.formatAmount(grp['accTotalAging12'] || 0),
            GAgingAdv: this.ageing12HideAdv ? '' : this.formatAmount(grp['accTotalAdv'] || 0),
            GAgingTot: this.formatAmount(grp['accTotal'] || 0),
            _customer: grp['Customer'].map((c) => {
              return {
                ...c,
                contactinfo:
                  this.showContactRow ? `Tel No: ${c['custphone'] || ''}  Contact: ${c['custcontact'] || ''}` : '',
                Aging0: this.formatAmount(c['Aging0_NoAdv'] || '0'),
                Aging1: this.formatAmount(c['Aging1_NoAdv'] || '0'),
                Aging2: this.formatAmount(c['Aging2_NoAdv'] || '0'),
                Aging3: this.formatAmount(c['Aging3_NoAdv'] || '0'),
                Aging4: this.formatAmount(c['Aging4_NoAdv'] || '0'),
                Aging5: this.formatAmount(c['Aging5_NoAdv'] || '0'),
                Aging6: this.formatAmount(c['Aging6_NoAdv'] || '0'),
                Aging7: this.formatAmount(c['Aging7_NoAdv'] || '0'),
                Aging8: this.formatAmount(c['Aging8_NoAdv'] || '0'),
                Aging9: this.formatAmount(c['Aging9_NoAdv'] || '0'),
                Aging10: this.formatAmount(c['Aging10_NoAdv'] || '0'),
                Aging11: this.formatAmount(c['Aging11_NoAdv'] || '0'),
                Aging12: this.formatAmount(c['Aging12_NoAdv'] || '0'),
                AgingAdv: this.ageing12HideAdv ? '' : this.formatAmount(c['Advance'] || '0'),
                AgingTot: this.formatAmount(c['amtTotalAgeing'] || '0'),
                _docStatements:
                  reportType == 'Detail' ?
                    c['Statements'].map((s) => {
                      return {
                        txndate:
                          s['txnDate'] ?
                            new Date(s['txnDate']).formatString(this.$store.state['preferredDateFormat'])
                          : '',
                        refno: s['refno'] || '',
                        adv: s['sadv'] || '',
                        jobno: s['JobNo'],
                        docterm: this.ageing12ShowCrLimit ? s['cTerm'] || '' : '',
                        refAging0: this.formatAmount(s['Aging0_NoAdv'] || '0'),
                        refAging1: this.formatAmount(s['Aging1_NoAdv'] || '0'),
                        refAging2: this.formatAmount(s['Aging2_NoAdv'] || '0'),
                        refAging3: this.formatAmount(s['Aging3_NoAdv'] || '0'),
                        refAging4: this.formatAmount(s['Aging4_NoAdv'] || '0'),
                        refAging5: this.formatAmount(s['Aging5_NoAdv'] || '0'),
                        refAging6: this.formatAmount(s['Aging6_NoAdv'] || '0'),
                        refAging7: this.formatAmount(s['Aging7_NoAdv'] || '0'),
                        refAging8: this.formatAmount(s['Aging8_NoAdv'] || '0'),
                        refAging9: this.formatAmount(s['Aging9_NoAdv'] || '0'),
                        refAging10: this.formatAmount(s['Aging10_NoAdv'] || '0'),
                        refAging11: this.formatAmount(s['Aging11_NoAdv'] || '0'),
                        refAging12: this.formatAmount(s['Aging12_NoAdv'] || '0'),
                        refAgingAdv: this.ageing12HideAdv ? '' : this.formatAmount(s['Advance'] || 0),
                        balance: this.formatAmount(s['Balance'] || '0'),
                      };
                    })
                  : [],
              };
            }),
          };
        });
      }
      const formattedSOADetails = {
        General: soaDetails.General,
        StatementOfAccount: soaGroups,
      };
      return formattedSOADetails;
    },

    formatAmount: function (amount) {
      const strAmt = amount.toString();
      let formattedAmt = '';
      if (Number(amount) < 0) {
        formattedAmt = `(${this.$currencyFormat(strAmt.replaceAll(',', '')).replace('-', '')})`;
      } else {
        formattedAmt = this.$currencyFormat(strAmt.replaceAll(',', ''));
      }
      return formattedAmt;
    },

    getOptionKey: function () {
      let optionKey = '';
      if (this.selectedSelectBy) {
        switch (this.selectedSelectBy['key']) {
          case 'salesman':
            optionKey = 'Sales Person Code';
            break;

          case 'customer':
            optionKey = 'Customer Code';
            break;

          case 'glacc':
            optionKey = 'GL Acc Code';
            break;

          case 'status':
            optionKey = 'Status Code';
            break;
        }
      }
      return optionKey;
    },

    designerV3ShareClicked: function (shareLink) {
      if (this.designerV3ReportData[0].email) {
        this.emailSOAPopupToMails = this.designerV3ReportData[0].email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailSOAPopupToMails = [];
      }
      this.emailSOAPopupCCMails = [];
      this.emailSOAPopupBCCMails = [];

      if (this.designerV3ReportData.length > 1) {
        this.emailSOAPopupSubjectInputValue = 'Statement of Accounts';
        this.emailSOAPopupMessageText = `Statement of Accounts: ${shareLink}\n`;
      } else {
        this.emailSOAPopupSubjectInputValue = `Statement of Accounts\n`;
        this.emailSOAPopupMessageText = `Statement of Accounts: ${shareLink}\n`;
      }
      this.showEmailSOAPopup = true;
    },

    emailSOAPopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailSOAPopup = false;
        return;
      }

      if (this.emailSOAPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Email', "Please enter at least one valid 'To' email address", [
          { text: 'OK' },
        ]);
        return;
      }

      if (!this.emailSOAPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedWithoutSubject === 'Cancel') return;
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'SOA',
            docno: this.designerV3ReportData[0]['custNo'],
            docnofieldname: '',
            recipient: this.emailSOAPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailSOAPopupCCMails.map((mail) => mail.email).join(';'),
            bcc: this.emailSOAPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailSOAPopupSubjectInputValue.trim(),
            message: this.emailSOAPopupMessageText,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/StatementOfAccounts/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailSOAPopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },
  },
  async mounted() {
    const mountedPromises = [];
    let promisesFulfilled = 0;
    this.tableLoading = true;

    mountedPromises.push(
      this.$apiCallV2('/StatementOfAccounts/GetStatementOfAccSelection', { listname: 'customer' }).then((result) => {
        this.customerList = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/StatementOfAccounts/GetStatementOfAccSelection', { listname: 'glacc' }).then((result) => {
        this.glAccList = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/StatementOfAccounts/GetStatementOfAccSelection', { listname: 'status' }).then((result) => {
        this.statusList = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/StatementOfAccounts/GetStatementOfAccSelection', { listname: 'salesman' }).then((result) => {
        this.salesPersonList = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/StatementOfAccounts/GetStatementOfAccSelection', { listname: 'jobtypeconfig' }).then(
        (result) => {
          this.docConfigList = result;
          this.$topLoading(++promisesFulfilled / mountedPromises.length);
        },
      ),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=LetterHead_SOA').then((result) => {
        this.showCoLetterHeadChecked = result?.[0]?.['SettingsValue'].toUpperCase() == 'TRUE';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FINAR_AsAtDate').then((result) => {
        this.rcdsTillCurrMthChecked = result?.[0]?.['SettingsValue'].toUpperCase() != 'SELECTEDDATE';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=SOA_ExcCurrentFmt').then((result) => {
        this.includeCurrMthChecked = result?.[0]?.['SettingsValue'].toUpperCase() == 'FALSE';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=SOA_OffsetFmt').then((result) => {
        this.offsetFormatChecked = result?.[0]?.['SettingsValue'].toUpperCase() == 'TRUE';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOAShowCTerm').then((result) => {
        this.showTermColumn = result?.[0]?.['SettingsValue'] == '1';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOAAgShowContact').then((result) => {
        this.showContactRow = result?.[0]?.['SettingsValue'] != '0'; // default 1
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOAAgDetShowJob').then((result) => {
        this.jobnoColSetting = result?.[0]?.['SettingsValue'] || '1'; // default 1
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOAAg6HeaderFmt').then((result) => {
        this.ageing6HeaderFmt = result?.[0]?.['SettingsValue'];
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOACustSummary').then((result) => {
        this.custCopyAgeingSummaryFormat = result?.[0]?.['SettingsValue'];
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOACustInterest').then((result) => {
        this.custCopyShowInterestStmt = result?.[0]?.['SettingsValue'] == '1';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOAAg12HideAdv').then((result) => {
        this.ageing12HideAdv = result?.[0]?.['SettingsValue'] == '1';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOAAg12ShowCrLmt').then((result) => {
        this.ageing12ShowCrLimit = result?.[0]?.['SettingsValue'] == '1';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_SOAAg6Show5buck').then((result) => {
        this.ageing6Show5Buckets = result?.[0]?.['SettingsValue'] == '1';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userDetails = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises).then(() => {
      this.tableLoading = false;
    });

    this.selectedSelectBy = this.selectByOptions[0];
    this.selectionTableData = [...this.customerList];
    this.selectedReportFormat = this.reportFormatItems[0];

    // set default as at date as last day of the month
    this.asAtDate = this.getLastDateOfMonth(new Date());

    this.selectedReportFormat = this.reportFormatItems[0];
    this.selectedTotalAging = this.totalAgingItems[0];
  },
  watch: {
    offsetFormatChecked: function (val) {
      if (val) {
        this.includeCurrMthChecked = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.print-soa-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .soa-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;
    width: 50%;
    height: 100%;
    display: flex;
    flex-direction: column;

    > .soa-tablebox-container {
      margin-top: 5px;
      //height: 45%;
      flex: 1;
      overflow-y: auto;
    }

    .date-options-container {
      width: 50%;
      margin-left: 10px;
      height: 80%;
      align-self: center;
    }
  }

  > .soa-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }

  > .email-soa-popup {
    .email-soa-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
